'use client'

import { clsx } from 'clsx'
import { type FC, useRef } from 'react'

import { Link } from 'base-ui'

import { type AttributeGroupData } from '../../../product-page-context/AttributeGroup'
import { useProductPageContext } from '../../../productPageContext'
import { ProductPageAttributeSelector } from '../ProductPageAttributeSelector'
import { useAttributeSelectionCollapsible } from '../useAttributeSelectionCollapsible'
import { useScrollElementIntoView } from '../useScrollElementIntoView'

import { ProductPageColorTypeAttributeHead } from './ProductPageColorTypeAttributeHead'
import { ProductPageColorTypeAttributePreview } from './ProductPageColorTypeAttributePreview'

interface ProductPageColorTypeAttributeGroupProps {
  attributeGroup: AttributeGroupData
  className?: string
  isSingleGroup: boolean
}

export const ProductPageColorTypeAttributeGroup: FC<
  ProductPageColorTypeAttributeGroupProps
> = ({ attributeGroup, className, isSingleGroup }) => {
  const { attributeSelectionControls } = useProductPageContext()

  const attributeNotSelectedError =
    attributeSelectionControls.attributeNotSelectedErrorControls.getShouldShowAttributeMustBeSelectedErrorForGroup(
      attributeGroup,
    )

  const {
    metaAttributeData,
    selectionEntries,
    shouldDisplayOnlySelectedValue,
  } = attributeGroup

  const selectedEntry = selectionEntries.find((it) => it.isSelected)

  const collapsibleData = useAttributeSelectionCollapsible({
    isSingleGroup,
    metaAttributeData,
    selectionEntries,
  })

  const scrollableElementRef = useRef(null)

  const handleScrollToElement = useScrollElementIntoView(scrollableElementRef)

  return (
    <div className={clsx(className, 'flex flex-col')}>
      <ProductPageColorTypeAttributeHead
        metaAttribute={metaAttributeData}
        selectedEntry={selectedEntry}
      />
      {!shouldDisplayOnlySelectedValue && (
        <>
          <div
            className="flex flex-row gap-2 overflow-x-auto pb-2.5 scrollbar-hide tablet:grid desktop:grid-cols-2"
            ref={scrollableElementRef}
          >
            {selectionEntries.map((entry, index) => {
              return (
                <ProductPageAttributeSelector
                  className={clsx(
                    collapsibleData.maxVisibleIndex <= index && 'tablet:hidden',
                  )}
                  error={attributeNotSelectedError}
                  key={entry.attribute.value}
                  label={
                    <>
                      <ProductPageColorTypeAttributePreview
                        selectionEntry={entry}
                      />
                      {entry.attribute.value}
                    </>
                  }
                  onScrollIntoView={handleScrollToElement}
                  selectionEntry={entry}
                />
              )
            })}
          </div>
          {collapsibleData.showLabel ? (
            <Link
              className="mt-3 hidden w-full flex-row items-center justify-center tablet:flex"
              color="brand"
              onClick={collapsibleData.handleLabelClick}
              slots={{ root: 'button' }}
              type="button"
            >
              {collapsibleData.labelContent}
            </Link>
          ) : null}
        </>
      )}
    </div>
  )
}
